import type { ReactNode } from "react";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { RefreshCw } from "lucide-react";

interface EnterprisePageHeaderProps {
    title: string;
    subtitle?: ReactNode;
    icon?: ReactNode;
    badge?: string;
    isLoading?: boolean;
    onRefresh?: () => void;
    actions?: ReactNode;
}

export function EnterprisePageHeader({
    title,
    subtitle,
    icon,
    badge,
    isLoading = false,
    onRefresh,
    actions,
}: EnterprisePageHeaderProps) {
    return (
        <section className="rounded-2xl border border-border/70 bg-gradient-to-br from-primary/10 via-card/70 to-card/40 p-4 shadow-sm backdrop-blur-sm sm:p-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                        {badge ? <Badge variant="outline">{badge}</Badge> : null}
                    </div>
                    <div className="flex items-start gap-3">
                        {icon ? <div className="mt-1 text-primary">{icon}</div> : null}
                        <div>
                            <h1 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
                                {title}
                            </h1>
                            {subtitle ? (
                                <p className="mt-1 text-sm text-muted-foreground sm:text-base">{subtitle}</p>
                            ) : null}
                        </div>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    {actions}
                    {onRefresh ? (
                        <Button onClick={onRefresh} disabled={isLoading} variant="outline" size="sm">
                            <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
                            {isLoading ? "Refreshing..." : "Refresh"}
                        </Button>
                    ) : null}
                </div>
            </div>
        </section>
    );
}
